import React from "react";
import type { ColumnsType } from "antd/es/table";
import { Table, Tooltip } from "antd";

import { humanReadableDate } from "../../../../../helper/dateTimeHelper";
import type { Lead, Pagination } from "../../../../../core/data/redux/leads-queue/types";

interface LeadsQueueTableProps {
    leads: Lead[];
    pagination: Pagination;
    tableLoading: boolean;

    selectedRowKeys: React.Key[];
    onSelectedRowKeysChange: (keys: React.Key[]) => void;

    onPageChange: (page: number) => void | Promise<void>;
}

const LeadsQueueTable: React.FC<LeadsQueueTableProps> = ({
    leads,
    pagination,
    tableLoading,
    selectedRowKeys,
    onSelectedRowKeysChange,
    onPageChange,
}) => {
    const columns: ColumnsType<Lead> = [
        {
            title: "Lead Name",
            dataIndex: "LeadName",
            key: "LeadName",
            width: 220,
            sorter: (a, b) => a.LeadName.localeCompare(b.LeadName),
            render: (text) => <h6 className="fw-medium fs-14">{text}</h6>,
        },
        {
            title: "Phone",
            dataIndex: "Phone",
            key: "Phone",
            width: 150,
            render: (text) => (
                <span>
                    <i className="ti ti-phone me-1 text-muted" />
                    {text}
                </span>
            ),
            sorter: (a, b) => a.Phone.localeCompare(b.Phone),
        },
        {
            title: "Email",
            dataIndex: "Email",
            key: "Email",
            width: 200,
            render: (text) => <span className="text-muted">{text || "N/A"}</span>,
            sorter: (a, b) => (a.Email || "").localeCompare(b.Email || ""),
        },
        {
            title: "Lead Source",
            dataIndex: "lead_source_child",
            key: "lead_source_child",
            width: 140,
            render: (text) => (
                <span className="badge badge-info-transparent">{text || "N/A"}</span>
            ),
            sorter: (a, b) => (a.lead_source_child || "").localeCompare(b.lead_source_child || ""),
        },
        {
            title: "City",
            dataIndex: "response",
            key: "city",
            width: 120,
            render: (response: any) => <span>{response?.City || "N/A"}</span>,
            sorter: (a, b) => (a.response?.City || "").localeCompare(b.response?.City || ""),
        },
        {
            title: "State",
            dataIndex: "response",
            key: "state",
            width: 120,
            render: (response: any) => <span>{response?.State || "N/A"}</span>,
            sorter: (a, b) => (a.response?.State || "").localeCompare(b.response?.State || ""),
        },
        {
            title: "Created Date",
            dataIndex: "CreatedDate",
            key: "CreatedDate",
            width: 185,
            sorter: (a, b) =>
                new Date(a.CreatedDate).getTime() - new Date(b.CreatedDate).getTime(),
            render: (date) => (
                <Tooltip title={humanReadableDate(date, true, undefined, undefined, false)}>
                    <div className="text-muted small">
                        <i className="ti ti-calendar me-1" />
                        {humanReadableDate(date, true, undefined, undefined, false)}
                    </div>
                </Tooltip>
            ),
        },
    ];

    const rowSelection = {
        selectedRowKeys,
        onChange: (keys: React.Key[]) => onSelectedRowKeysChange(keys),
    };

    return (
        <div className="card-body p-0 mb-3">
            {tableLoading ? (
                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 400 }}>
                    <div className="text-center">
                        <div
                            className="spinner-border text-primary"
                            role="status"
                            style={{ width: "3rem", height: "3rem" }}
                        >
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <p className="mt-3 text-muted">Loading leads...</p>
                    </div>
                </div>
            ) : leads.length > 0 ? (
                <Table
                    className="datanew dataTable no-footer"
                    dataSource={leads}
                    columns={columns}
                    rowSelection={rowSelection}
                    pagination={{
                        current: pagination.current_page,
                        pageSize: pagination.per_page,
                        total: pagination.total,
                        onChange: (page) => onPageChange(page),
                        showTotal: (total: number, range: [number, number]) =>
                            `Showing ${range[0]} - ${range[1]} of ${total} entries`,
                        showSizeChanger: false,
                    }}
                    scroll={{ x: 1200 }}
                    rowKey="id"
                />
            ) : (
                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 400 }}>
                    <div className="text-center">
                        <div className="mb-3">
                            <i className="ti ti-inbox text-muted" style={{ fontSize: "4rem" }} />
                        </div>
                        <h5 className="mt-3 mb-2 fw-semibold">No Leads Found</h5>
                        <p className="text-muted mb-0">
                            No leads are currently in the queue for the selected filters
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default LeadsQueueTable;
